"use client";

import { useCallback, useEffect, useRef } from "react";

type WebkitWindow = Window & typeof globalThis & {
  webkitAudioContext?: typeof AudioContext;
};

export type LegacyAudioCue = "land" | "clear";

export interface LegacyAudioAsset {
  readonly durationSeconds: number;
  readonly path: string;
  readonly trigger: string;
}

/**
 * The two battle sounds confirmed by section 7.4 of the reproduction spec.
 * Files are the mono 44.1 kHz extracts from the user-provided main SWF; keeping
 * the metadata here makes the documented duration/trigger contract testable.
 */
export const LEGACY_AUDIO_ASSETS = {
  land: {
    durationSeconds: 0.9142,
    path: "/audio/bwwk-land.mp3",
    trigger: "lock-without-line-clear",
  },
  clear: {
    durationSeconds: 1.2278,
    path: "/audio/bwwk-clear.mp3",
    trigger: "lock-with-one-or-more-lines-cleared",
  },
} as const satisfies Record<LegacyAudioCue, LegacyAudioAsset>;

const LEGACY_AUDIO_CUES = Object.keys(LEGACY_AUDIO_ASSETS) as LegacyAudioCue[];

export type LegacyAudioBuffers = Partial<Record<LegacyAudioCue, AudioBuffer>>;

export type LegacyAudioFetcher = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

export interface LegacyAudioDecoder {
  decodeAudioData(audioData: ArrayBuffer): Promise<AudioBuffer>;
}

export function legacyAudioCueForLock(clearedLines: number): LegacyAudioCue {
  return clearedLines > 0 ? "clear" : "land";
}

/**
 * Decode both samples once. Each file is isolated so one unavailable sample
 * never delays or disables the other, and callers can safely retry preload.
 */
export async function loadLegacyAudioBuffers(
  decoder: LegacyAudioDecoder,
  fetchAudio: LegacyAudioFetcher = fetch,
): Promise<LegacyAudioBuffers> {
  const decoded = await Promise.all(
    LEGACY_AUDIO_CUES.map(async (cue) => {
      try {
        const response = await fetchAudio(LEGACY_AUDIO_ASSETS[cue].path, {
          cache: "force-cache",
        });
        if (!response.ok) return [cue, undefined] as const;
        const bytes = await response.arrayBuffer();
        const buffer = await decoder.decodeAudioData(bytes.slice(0));
        return [cue, buffer] as const;
      } catch {
        return [cue, undefined] as const;
      }
    }),
  );

  const buffers: LegacyAudioBuffers = {};
  for (const [cue, buffer] of decoded) {
    if (buffer) buffers[cue] = buffer;
  }
  return buffers;
}

export function useLegacyAudio() {
  const contextRef = useRef<AudioContext | null>(null);
  const buffersRef = useRef<LegacyAudioBuffers>({});
  const preloadRef = useRef<Promise<void> | null>(null);

  const getContext = useCallback(() => {
    if (contextRef.current) return contextRef.current;
    const AudioContextClass = window.AudioContext ?? (window as WebkitWindow).webkitAudioContext;
    if (!AudioContextClass) return null;
    contextRef.current = new AudioContextClass();
    return contextRef.current;
  }, []);

  const preload = useCallback(async (context: AudioContext) => {
    if (LEGACY_AUDIO_CUES.every((cue) => buffersRef.current[cue])) return;
    if (!preloadRef.current) {
      preloadRef.current = loadLegacyAudioBuffers(context)
        .then((buffers) => {
          Object.assign(buffersRef.current, buffers);
        })
        .finally(() => {
          preloadRef.current = null;
        });
    }
    await preloadRef.current;
  }, []);

  const unlock = useCallback(async () => {
    const context = getContext();
    if (!context) return;
    if (context.state === "suspended") await context.resume();
    if (context.state !== "closed") await preload(context);
  }, [getContext, preload]);

  const playCue = useCallback(
    (cue: LegacyAudioCue) => {
      const context = getContext();
      if (!context || context.state !== "running") return;

      const buffer = buffersRef.current[cue];
      if (!buffer) {
        void preload(context);
        return;
      }

      // A fresh source for every trigger preserves the original overlapping
      // attachSound/start behavior when both boards settle close together.
      const source = context.createBufferSource();
      source.buffer = buffer;
      source.connect(context.destination);
      source.start(context.currentTime);
    },
    [getContext, preload],
  );

  const playLock = useCallback(
    (clearedLines: number) => {
      playCue(legacyAudioCueForLock(clearedLines));
    },
    [playCue],
  );

  useEffect(
    () => () => {
      buffersRef.current = {};
      preloadRef.current = null;
      const context = contextRef.current;
      contextRef.current = null;
      if (context && context.state !== "closed") void context.close();
    },
    [],
  );

  return { playLock, unlock };
}
